import { z } from "zod";

const SegmentScopes = ["mine", "shared-with-me"] as const;

export type SegmentScope = (typeof SegmentScopes)[number];

const SegmentOwnerSchema = z.object({
    id: z.string(),
    displayName: z.string(),
    imageUrl: z.string().optional()
});

export const SegmentSchema = z.object({
    id: z.string(),
    name: z.string(),
    tags: z.array(z.string()),
    owner: SegmentOwnerSchema.optional(),
    memberCount: z.number().optional(),
    isStarred: z.boolean().optional(),
    isEditable: z.boolean().optional()
});

export type Segment = z.infer<typeof SegmentSchema>;

export const SEGMENT_NAME_MAX = 100;

const EmployeeFieldTypes = ["option", "date", "number", "person", "personReference"] as const;

export type EmployeeFieldType = (typeof EmployeeFieldTypes)[number];

export const EmployeeFieldSchema = z.object({
    id: z.string(),
    displayName: z.string(),
    type: z.enum(EmployeeFieldTypes),
    systemAttributeId: z.string().optional()
});

export type EmployeeField = z.infer<typeof EmployeeFieldSchema>;

export const EmployeeFieldValueSchema = z.object({
    value: z.string(),
    label: z.string()
});

export type EmployeeFieldValue = z.infer<typeof EmployeeFieldValueSchema>;

export const RuleOperators = ["Equal", "NotEqual", "AnyOf", "NoneOf", "GreaterThan", "LowerThan", "Between", "NotSet", "InReportingLineOf", "OccurredLessThan", "OccurredMoreThan", "OccurredBetween", "OccursInLessThan", "OccursInMoreThan", "OccursInBetween"] as const;

export type RuleOperator = (typeof RuleOperators)[number];

export const PeriodTypes = ["Years", "Months", "Days"] as const;

export type PeriodType = (typeof PeriodTypes)[number];

export const SegmentRuleSchema = z.object({
    propertyId: z.string(),
    operator: z.enum(RuleOperators),
    values: z.array(z.string()),
    periodType: z.enum(PeriodTypes).optional()
});

export type SegmentRule = z.infer<typeof SegmentRuleSchema>;

export const MatchTypes = ["all", "any"] as const;

export type MatchType = (typeof MatchTypes)[number];

export const AdHocFilterSchema = z.object({
    matchType: z.enum(MatchTypes),
    rules: z.array(SegmentRuleSchema)
});

export type AdHocFilter = z.infer<typeof AdHocFilterSchema>;

export const SegmentPersonSchema = z.object({
    id: z.string(),
    displayName: z.string(),
    jobTitle: z.string(),
    imageUrl: z.string().optional()
});

export type SegmentPerson = z.infer<typeof SegmentPersonSchema>;

export const SegmentDetailSchema = z.object({
    id: z.string(),
    name: z.string(),
    isRuleBased: z.boolean(),
    isEditable: z.boolean(),
    matchType: z.enum(MatchTypes),
    rules: z.array(SegmentRuleSchema),
    requiredRules: z.array(SegmentRuleSchema),
    hasUnlistedRequiredRules: z.boolean(),
    hasUnlistedRules: z.boolean(),
    ownerDisplayName: z.string().optional(),
    members: z.array(SegmentPersonSchema),
    tags: z.array(z.string())
});

export type SegmentDetail = z.infer<typeof SegmentDetailSchema>;

export const RulePersonSchema = z.object({
    id: z.string(),
    displayName: z.string(),
    isDeactivated: z.boolean().optional()
});

export type RulePerson = z.infer<typeof RulePersonSchema>;

export const SegmentMemberSchema = z.object({
    id: z.string(),
    displayName: z.string(),
    jobTitle: z.string().optional(),
    imageUrl: z.string().optional()
});

export type SegmentMember = z.infer<typeof SegmentMemberSchema>;
